import { useState, useMemo } from 'react';
import { TrendingUp, AlertCircle, Users, Filter } from 'lucide-react';
import { Card } from '@components/ui';
import clsx from 'clsx';
import { CategoryGrid } from '../shared';

/**
 * GelirAnalizi — Gelir Analizi Sekmesi
 * Filtreler: Gelir Kategorisi, Tahsilat Durumu
 */
export default function GelirAnalizi({ summary, receivables, formatCurrency, formatDate }) {
    const totalIncome = summary.income?.total || 0;

    // Filtre: tahsilat durumu
    const [statusFilter, setStatusFilter] = useState('all'); // all, normal, overdue

    // Gelir kategorileri
    const incomeItems = [
        { label: 'Daire Satışları', value: summary.income?.apartmentSales || 0, icon: '🏠' },
        { label: 'Taksit Tahsilatları', value: summary.income?.installments || 0, icon: '💳' },
        { label: 'Kapora / Diğer', value: summary.income?.other || 0, icon: '💰' },
    ].filter(i => i.value > 0).sort((a, b) => b.value - a.value);

    // Filtrelenmiş alacaklar
    const filteredReceivables = useMemo(() => {
        if (statusFilter === 'all') return receivables;
        return receivables.filter(r => r.status === statusFilter);
    }, [receivables, statusFilter]);

    return (
        <div className="space-y-6">
            {/* Toplam Gelir Kartı + Dağılım */}
            <Card padding="lg">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                        <TrendingUp size={24} className="text-success" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-text-primary">Toplam Gelir</h2>
                        <p className="text-3xl font-bold text-success">{formatCurrency(totalIncome)}</p>
                    </div>
                </div>
                <CategoryGrid items={incomeItems} total={totalIncome} variant="income" formatCurrency={formatCurrency} />
            </Card>

            {/* Tahsilat Durumu */}
            <Card padding="lg">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
                    <Users size={18} className="text-text-muted" />
                    Tahsilat Durumu
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-success/5 rounded-xl border border-success/20">
                        <p className="text-sm text-text-muted">Tahsil Edildi</p>
                        <p className="text-2xl font-bold text-text-primary mt-1">{formatCurrency(summary.collections?.collected || 0)}</p>
                    </div>
                    <div className="p-4 bg-warning/5 rounded-xl border border-warning/20">
                        <p className="text-sm text-text-muted">Bekleyen</p>
                        <p className="text-2xl font-bold text-warning mt-1">{formatCurrency(summary.collections?.pending || 0)}</p>
                    </div>
                    <div className="p-4 bg-error/5 rounded-xl border border-error/20">
                        <p className="text-sm text-text-muted">Gecikmiş</p>
                        <p className="text-2xl font-bold text-error mt-1">{formatCurrency(summary.collections?.overdue || 0)}</p>
                    </div>
                </div>
            </Card>

            {/* Alacaklar — Filtrelenebilir */}
            <Card padding="lg">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                        <TrendingUp size={18} className="text-success" />
                        Kişilerden Alacaklar
                        <span className="text-sm font-normal text-text-tertiary">({filteredReceivables.length})</span>
                    </h3>
                    {/* Durum Filtresi */}
                    <div className="flex items-center gap-1 bg-bg-tertiary rounded-lg p-1">
                        {[
                            { key: 'all', label: 'Tümü' },
                            { key: 'normal', label: 'Normal' },
                            { key: 'overdue', label: 'Gecikmiş' },
                        ].map(opt => (
                            <button key={opt.key} onClick={() => setStatusFilter(opt.key)}
                                className={clsx("px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                                    statusFilter === opt.key
                                        ? "bg-bg-secondary text-text-primary shadow-sm"
                                        : "text-text-muted hover:text-text-secondary"
                                )}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    {filteredReceivables.map(item => (
                        <div key={item.id} className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg hover:bg-bg-hover transition-colors">
                            <div>
                                <p className="text-sm font-medium text-text-primary">{item.name}</p>
                                <p className="text-xs text-text-muted">Son ödeme: {formatDate(item.lastPaymentDate)}</p>
                            </div>
                            <div className="text-right">
                                <p className={clsx("font-semibold", item.status === 'overdue' ? 'text-error' : 'text-text-primary')}>
                                    {formatCurrency(item.totalRemaining)}
                                </p>
                                {item.status === 'overdue' && (
                                    <span className="text-xs text-error flex items-center gap-1 justify-end"><AlertCircle size={12} /> Gecikmiş</span>
                                )}
                            </div>
                        </div>
                    ))}
                    {filteredReceivables.length === 0 && (
                        <p className="text-sm text-text-muted text-center py-4">Seçili filtreye uygun alacak yok.</p>
                    )}
                </div>
            </Card>
        </div>
    );
}
